import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  DollarSign,
  Package,
  Percent,
  Plus,
  CheckCircle2,
  Search,
  X,
  Loader2,
} from 'lucide-react';
import {
  useFilterProducts,
  useProductSearch,
  useUpdateProduct,
  useBulkUpdateProductPrices,
} from '../../hooks/useProducts';
import type { BulkPriceUpdatePayload } from '../../services/productService';
import { Product, ProductFormData } from '../../types/Product';
import { formatCurrency } from '../../utils/formatters';
import { getErrorMessage } from '../../utils/errorMessages';
import { Toast } from '../common/Toast';
import { Column } from '../common/DataTable';

export const PriceChangeAndAlert: React.FC = () => {
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [updateType, setUpdateType] = useState<'price' | 'stock'>('price');
  // Alış fiyatı (unitPrice)
  const [purchasePriceChangeType, setPurchasePriceChangeType] = useState<'fixed' | 'percentage'>('fixed');
  const [purchasePriceChangeValue, setPurchasePriceChangeValue] = useState<string>('');
  // Satış fiyatı (retailPrice)
  const [retailPriceChangeType, setRetailPriceChangeType] = useState<'fixed' | 'percentage'>('fixed');
  const [retailPriceChangeValue, setRetailPriceChangeValue] = useState<string>('');
  const [stockChangeType, setStockChangeType] = useState<'add' | 'set'>('add');
  const [stockChangeValue, setStockChangeValue] = useState<string>('');
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  // Güncelleme modalı: bitene kadar kapatılamaz
  const [bulkUpdateModal, setBulkUpdateModal] = useState<{
    open: boolean;
    progress: number;
    total: number;
    done: boolean;
    succeeded: number;
    failed: number;
    failMessage?: string;
  }>({ open: false, progress: 0, total: 0, done: false, succeeded: 0, failed: 0 });

  const hasSearch = searchTerm.trim().length > 0;
  const { data: searchResults, isLoading: isSearchLoading } = useProductSearch(searchTerm.trim());
  const { data: filteredPage, isLoading: isFilterLoading } = useFilterProducts(
    hasSearch ? null : { page: 0, size: 1000 }
  );
  const updateProduct = useUpdateProduct();
  const bulkUpdatePrices = useBulkUpdateProductPrices();

  // Arama varsa backend search API sonucu, yoksa filter (tüm ürünler) sonucu
  const filteredProducts = useMemo(
    () => (hasSearch ? (searchResults ?? []) : (filteredPage?.content ?? [])),
    [hasSearch, searchResults, filteredPage?.content]
  );
  const isLoading = hasSearch ? isSearchLoading : isFilterLoading;

  const columns: Column<Product>[] = [
    { id: 'id', label: 'ID', minWidth: 50 },
    { id: 'name', label: 'Ürün Adı', minWidth: 150 },
    { id: 'brand', label: 'Marka', minWidth: 100 },
    { id: 'code', label: 'Barkod', minWidth: 100 },
    {
      id: 'unitPrice',
      label: 'Alış Fiyatı',
      minWidth: 110,
      align: 'right',
      format: (value: unknown) => formatCurrency(value as number),
    },
    {
      id: 'retailPrice',
      label: 'Satış Fiyatı',
      minWidth: 110,
      align: 'right',
      format: (value: unknown) => formatCurrency(value as number),
    },
    {
      id: 'quantity',
      label: 'Stok',
      minWidth: 80,
      align: 'right',
    },
  ];

  const handleProductSelect = useCallback((product: Product) => {
    setSelectedProducts((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) {
        return prev.filter((p) => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  }, []);

  // Shift+click: aralık seçimi; sürükle: birden fazla satırı seç/kaldır
  const lastClickIndexRef = useRef<number>(-1);
  const isDragSelectingRef = useRef(false);
  const dragAddRef = useRef(true);

  const handleRowMouseDown = useCallback(
    (product: Product, index: number, e: React.MouseEvent) => {
      if (e.button !== 0) return; // sadece sol tıklama
      if (e.shiftKey) {
        const last = lastClickIndexRef.current;
        if (last >= 0 && last < filteredProducts.length) {
          const start = Math.min(last, index);
          const end = Math.max(last, index);
          const range = filteredProducts.slice(start, end + 1);
          setSelectedProducts((prev) => {
            const prevIds = new Set(prev.map((p) => p.id));
            const merged = [...prev];
            for (const p of range) {
              if (!prevIds.has(p.id)) {
                merged.push(p);
                prevIds.add(p.id);
              }
            }
            return merged;
          });
        }
        lastClickIndexRef.current = index;
        return;
      }
      const isSelected = selectedProducts.some((p) => p.id === product.id);
      isDragSelectingRef.current = true;
      dragAddRef.current = !isSelected;
      handleProductSelect(product);
      lastClickIndexRef.current = index;
    },
    [filteredProducts, selectedProducts, handleProductSelect]
  );

  const handleRowMouseEnter = useCallback(
    (product: Product) => {
      if (!isDragSelectingRef.current) return;
      const isSelected = selectedProducts.some((p) => p.id === product.id);
      if (dragAddRef.current && !isSelected) {
        setSelectedProducts((prev) => [...prev, product]);
      } else if (!dragAddRef.current && isSelected) {
        setSelectedProducts((prev) => prev.filter((p) => p.id !== product.id));
      }
    },
    [selectedProducts]
  );

  React.useEffect(() => {
    const onMouseUp = () => {
      isDragSelectingRef.current = false;
    };
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  const handleSelectAll = () => {
    // Select all filtered products
    const allFilteredIds = filteredProducts.map((p: Product) => p.id);
    const allSelected = allFilteredIds.every((id: number) =>
      selectedProducts.some((p) => p.id === id)
    );

    if (allSelected) {
      // Remove all filtered products from selection
      setSelectedProducts((prev) =>
        prev.filter((p) => !allFilteredIds.includes(p.id))
      );
    } else {
      // Add all filtered products to selection
      const newSelections = filteredProducts.filter(
        (p: Product) => !selectedProducts.some((sp) => sp.id === p.id)
      );
      setSelectedProducts((prev) => [...prev, ...newSelections]);
    }
  };

  /** Yüzde veya sabit artış/azalış ile yeni fiyat hesaplar. */
  const applyPriceChange = (
    currentPrice: number,
    changeType: 'fixed' | 'percentage',
    changeValue: string
  ): number => {
    if (!changeValue || !changeValue.trim()) return currentPrice;
    const value = parseFloat(changeValue);
    if (isNaN(value)) return currentPrice;
    if (changeType === 'percentage') {
      return currentPrice * (1 + value / 100);
    }
    return currentPrice + value;
  };

  const calculateNewStock = (currentStock: number): number => {
    if (!stockChangeValue) return currentStock;
    const value = parseInt(stockChangeValue);
    if (isNaN(value)) return currentStock;

    if (stockChangeType === 'add') {
      return currentStock + value;
    } else {
      return value;
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedProducts.length === 0) {
      setToast({
        open: true,
        message: 'Lütfen en az bir ürün seçin',
        severity: 'error',
      });
      return;
    }

    if (updateType === 'price') {
      const productIds = selectedProducts.map((p) => p.id);
      const payload: BulkPriceUpdatePayload = { productIds };
      const pTrim = purchasePriceChangeValue.trim();
      const rTrim = retailPriceChangeValue.trim();
      if (pTrim) {
        const v = parseFloat(pTrim.replace(',', '.'));
        if (!Number.isFinite(v)) {
          setToast({
            open: true,
            message: 'Alış fiyatı için geçerli bir sayı girin',
            severity: 'error',
          });
          return;
        }
        payload.purchasePriceUpdate = {
          mode: purchasePriceChangeType === 'percentage' ? 'PERCENT' : 'ABSOLUTE',
          value: v,
        };
      }
      if (rTrim) {
        const v = parseFloat(rTrim.replace(',', '.'));
        if (!Number.isFinite(v)) {
          setToast({
            open: true,
            message: 'Satış fiyatı için geçerli bir sayı girin',
            severity: 'error',
          });
          return;
        }
        payload.salePriceUpdate = {
          mode: retailPriceChangeType === 'percentage' ? 'PERCENT' : 'ABSOLUTE',
          value: v,
        };
      }
      if (!payload.purchasePriceUpdate && !payload.salePriceUpdate) {
        setToast({
          open: true,
          message: 'En az bir fiyat alanı doldurun',
          severity: 'error',
        });
        return;
      }

      const invalid = selectedProducts.find((product) => {
        const newUnit =
          purchasePriceChangeValue
            ? applyPriceChange(product.unitPrice, purchasePriceChangeType, purchasePriceChangeValue)
            : product.unitPrice;
        const newRetail =
          retailPriceChangeValue
            ? applyPriceChange(product.retailPrice, retailPriceChangeType, retailPriceChangeValue)
            : product.retailPrice;
        return newRetail < newUnit;
      });
      if (invalid) {
        setToast({
          open: true,
          message: 'Satış fiyatı alış fiyatından düşük olamaz. Lütfen değerleri kontrol edin.',
          severity: 'error',
        });
        return;
      }

      setBulkUpdateModal({
        open: true,
        progress: 0,
        total: 1,
        done: false,
        succeeded: 0,
        failed: 0,
      });

      try {
        const result = await bulkUpdatePrices.mutateAsync(payload);
        setBulkUpdateModal({
          open: true,
          progress: 1,
          total: 1,
          done: true,
          succeeded: result.updated,
          failed: 0,
        });
        setToast({
          open: true,
          message: `${result.updated} ürün başarıyla güncellendi`,
          severity: 'success',
        });
        setSelectedProducts([]);
        setPurchasePriceChangeValue('');
        setRetailPriceChangeValue('');
      } catch (error: unknown) {
        setBulkUpdateModal((m) => ({
          ...m,
          done: true,
          progress: 0,
          failed: 1,
          failMessage: getErrorMessage(error, 'Güncelleme sırasında hata oluştu'),
        }));
        setToast({
          open: true,
          message: getErrorMessage(error, 'Güncelleme sırasında hata oluştu'),
          severity: 'error',
        });
      }
      return;
    }

    const total = selectedProducts.length;
    setBulkUpdateModal({
      open: true,
      progress: 0,
      total,
      done: false,
      succeeded: 0,
      failed: 0,
    });

    try {
      const failed: { id: number; name: string; error: string }[] = [];
      let succeeded = 0;

      for (const product of selectedProducts) {
        const updateData: ProductFormData = {
          name: product.name,
          color: product.color,
          quantity: product.quantity,
          brand: product.brand,
          code: product.code,
          purchaseDate: product.purchaseDate,
          unitPrice: product.unitPrice,
          retailPrice: product.retailPrice,
          description: product.description,
        };

        if (stockChangeValue) {
          updateData.quantity = calculateNewStock(product.quantity);
        }

        try {
          await updateProduct.mutateAsync({ id: product.id, data: updateData });
          succeeded++;
        } catch (err) {
          failed.push({
            id: product.id,
            name: product.name,
            error: getErrorMessage(err, 'Bilinmeyen hata'),
          });
        }
        const currentSucceeded = succeeded;
        const currentFailed = failed.length;
        setBulkUpdateModal((m) => ({
          ...m,
          progress: currentSucceeded + currentFailed,
          succeeded: currentSucceeded,
          failed: currentFailed,
        }));
      }

      const failMessage =
        failed.length > 0
          ? failed.length <= 2
            ? failed.map((f) => `${f.name} (${f.error})`).join('; ')
            : `${failed.length} ürün başarısız. ${failed[0].error}`
          : undefined;

      setBulkUpdateModal((m) => ({
        ...m,
        done: true,
        succeeded,
        failed: failed.length,
        failMessage,
      }));

      if (failed.length === 0) {
        setToast({
          open: true,
          message: `${succeeded} ürün başarıyla güncellendi`,
          severity: 'success',
        });
        setSelectedProducts([]);
        setPurchasePriceChangeValue('');
        setRetailPriceChangeValue('');
        setStockChangeValue('');
      } else {
        setToast({
          open: true,
          message: `${succeeded} güncellendi, ${failed.length} başarısız.`,
          severity: 'error',
        });
        setSelectedProducts((prev) => prev.filter((p) => failed.some((f) => f.id === p.id)));
      }
    } catch (error: unknown) {
      setBulkUpdateModal((m) => ({
        ...m,
        done: true,
        failed: m.total,
        failMessage: getErrorMessage(error, 'Güncelleme sırasında hata oluştu'),
      }));
      setToast({
        open: true,
        message: getErrorMessage(error, 'Güncelleme sırasında hata oluştu'),
        severity: 'error',
      });
    }
  };

  const closeBulkUpdateModal = () => {
    setBulkUpdateModal({ open: false, progress: 0, total: 0, done: false, succeeded: 0, failed: 0 });
  };

  const previewUpdates = () => {
    if (selectedProducts.length === 0) return null;

    return selectedProducts.map((product) => ({
      ...product,
      newUnitPrice:
        updateType === 'price' && purchasePriceChangeValue
          ? applyPriceChange(product.unitPrice, purchasePriceChangeType, purchasePriceChangeValue)
          : product.unitPrice,
      newRetailPrice:
        updateType === 'price' && retailPriceChangeValue
          ? applyPriceChange(product.retailPrice, retailPriceChangeType, retailPriceChangeValue)
          : product.retailPrice,
      newStock:
        updateType === 'stock'
          ? calculateNewStock(product.quantity)
          : product.quantity,
    }));
  };

  const preview = previewUpdates();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Product Selection */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search and Selection Info */}
          <div className="card p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-2">
                Ürün Ara
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-tertiary" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="İsim, marka, barkod veya renk ile ara..."
                  className="input pl-10 pr-10"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-smooth"
                  >
                    <X className="w-4 h-4 text-foreground-tertiary" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-1">
                  Ürün Seçimi
                </h3>
                <p className="text-xs text-foreground-secondary">
                  {selectedProducts.length} ürün seçildi
                  {searchTerm && (
                    <span className="ml-2">
                      ({filteredProducts.length} sonuç bulundu)
                    </span>
                  )}
                </p>
                <p className="text-xs text-foreground-tertiary mt-0.5">
                  Shift+tıklama ile aralık seçin; sürükleyerek birden fazla satır ekleyin veya kaldırın.
                </p>
              </div>
              <button
                onClick={handleSelectAll}
                className="btn-secondary text-xs"
              >
                {filteredProducts.every((p: Product) =>
                  selectedProducts.some((sp) => sp.id === p.id)
                )
                  ? 'Filtrelenmişleri Kaldır'
                  : 'Filtrelenmişleri Seç'}
              </button>
            </div>
          </div>

          {/* Product Table */}
          <div className="card">
            <div className="overflow-x-auto custom-scrollbar">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell w-12"></th>
                    {columns.map((column) => (
                      <th
                        key={String(column.id)}
                        className={`table-header-cell ${
                          column.align === 'right'
                            ? 'text-right'
                            : column.align === 'center'
                            ? 'text-center'
                            : 'text-left'
                        }`}
                        style={{ minWidth: column.minWidth }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={columns.length + 1} className="table-cell text-center py-8">
                        Yükleniyor...
                      </td>
                    </tr>
                  ) : filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 1} className="table-cell text-center py-8">
                        {searchTerm ? 'Arama sonucu bulunamadı' : 'Ürün bulunamadı'}
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((product: Product, index: number) => {
                      const isSelected = selectedProducts.some((p) => p.id === product.id);
                      return (
                        <tr
                          key={product.id}
                          onMouseDown={(e) => handleRowMouseDown(product, index, e)}
                          onMouseEnter={() => handleRowMouseEnter(product)}
                          className={`table-row cursor-pointer select-none ${
                            isSelected ? 'bg-accent/10 border-accent/30' : ''
                          }`}
                        >
                          <td className="table-cell">
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                isSelected
                                  ? 'bg-accent border-accent'
                                  : 'border-border hover:border-accent/50'
                              }`}
                            >
                              {isSelected && (
                                <CheckCircle2 className="w-3 h-3 text-white" />
                              )}
                            </div>
                          </td>
                          {columns.map((column) => {
                            const value =
                              column.id in product
                                ? product[column.id as keyof Product]
                                : '';
                            const cellContent = column.format
                              ? column.format(value, product)
                              : String(value ?? '');

                            return (
                              <td
                                key={String(column.id)}
                                className={`table-cell ${
                                  column.align === 'right'
                                    ? 'text-right'
                                    : column.align === 'center'
                                    ? 'text-center'
                                    : 'text-left'
                                }`}
                              >
                                {cellContent}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column - Update Form */}
        <div className="space-y-4">
          {/* Update Type Selection */}
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">
              Güncelleme Türü
            </h3>
            <div className="space-y-2">
              <button
                onClick={() => setUpdateType('price')}
                className={`w-full btn-secondary flex items-center gap-2 ${
                  updateType === 'price'
                    ? 'bg-accent/20 border-accent text-accent'
                    : ''
                }`}
              >
                <DollarSign className="w-4 h-4" />
                Sadece Fiyat
              </button>
              <button
                onClick={() => setUpdateType('stock')}
                className={`w-full btn-secondary flex items-center gap-2 ${
                  updateType === 'stock'
                    ? 'bg-accent/20 border-accent text-accent'
                    : ''
                }`}
              >
                <Package className="w-4 h-4" />
                Sadece Stok
              </button>
            </div>
          </div>

          {/* Alış Fiyatı Güncelleme */}
          {updateType === 'price' && (
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Alış Fiyatı Güncelleme
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-secondary mb-2">
                    Güncelleme Tipi
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPurchasePriceChangeType('fixed')}
                      className={`flex-1 btn-secondary text-xs ${
                        purchasePriceChangeType === 'fixed'
                          ? 'bg-accent/20 border-accent text-accent'
                          : ''
                      }`}
                    >
                      <DollarSign className="w-3 h-3 inline mr-1" />
                      Sabit
                    </button>
                    <button
                      onClick={() => setPurchasePriceChangeType('percentage')}
                      className={`flex-1 btn-secondary text-xs ${
                        purchasePriceChangeType === 'percentage'
                          ? 'bg-accent/20 border-accent text-accent'
                          : ''
                      }`}
                    >
                      <Percent className="w-3 h-3 inline mr-1" />
                      Yüzde
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-secondary mb-2">
                    {purchasePriceChangeType === 'percentage'
                      ? 'Yüzde Değişimi (%)'
                      : 'Sabit Tutar (₺)'}
                  </label>
                  <input
                    type="number"
                    value={purchasePriceChangeValue}
                    onChange={(e) => setPurchasePriceChangeValue(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder={
                      purchasePriceChangeType === 'percentage'
                        ? 'Örn: 10 (artış) veya -5 (azalış)'
                        : 'Örn: 50 (artış) veya -20 (azalış)'
                    }
                    className="input"
                    step={purchasePriceChangeType === 'percentage' ? '0.1' : '0.01'}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Satış Fiyatı Güncelleme */}
          {updateType === 'price' && (
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Satış Fiyatı Güncelleme
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-secondary mb-2">
                    Güncelleme Tipi
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setRetailPriceChangeType('fixed')}
                      className={`flex-1 btn-secondary text-xs ${
                        retailPriceChangeType === 'fixed'
                          ? 'bg-accent/20 border-accent text-accent'
                          : ''
                      }`}
                    >
                      <DollarSign className="w-3 h-3 inline mr-1" />
                      Sabit
                    </button>
                    <button
                      onClick={() => setRetailPriceChangeType('percentage')}
                      className={`flex-1 btn-secondary text-xs ${
                        retailPriceChangeType === 'percentage'
                          ? 'bg-accent/20 border-accent text-accent'
                          : ''
                      }`}
                    >
                      <Percent className="w-3 h-3 inline mr-1" />
                      Yüzde
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-secondary mb-2">
                    {retailPriceChangeType === 'percentage'
                      ? 'Yüzde Değişimi (%)'
                      : 'Sabit Tutar (₺)'}
                  </label>
                  <input
                    type="number"
                    value={retailPriceChangeValue}
                    onChange={(e) => setRetailPriceChangeValue(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder={
                      retailPriceChangeType === 'percentage'
                        ? 'Örn: 10 (artış) veya -5 (azalış)'
                        : 'Örn: 50 (artış) veya -20 (azalış)'
                    }
                    className="input"
                    step={retailPriceChangeType === 'percentage' ? '0.1' : '0.01'}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Stock Update Section */}
          {(updateType === 'stock') && (
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Stok Güncelleme
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-secondary mb-2">
                    Güncelleme Tipi
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setStockChangeType('add')}
                      className={`flex-1 btn-secondary text-xs ${
                        stockChangeType === 'add'
                          ? 'bg-accent/20 border-accent text-accent'
                          : ''
                      }`}
                    >
                      <Plus className="w-3 h-3 inline mr-1" />
                      Ekle
                    </button>
                    <button
                      onClick={() => setStockChangeType('set')}
                      className={`flex-1 btn-secondary text-xs ${
                        stockChangeType === 'set'
                          ? 'bg-accent/20 border-accent text-accent'
                          : ''
                      }`}
                    >
                      <CheckCircle2 className="w-3 h-3 inline mr-1" />
                      Ayarla
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-secondary mb-2">
                    {stockChangeType === 'add' ? 'Eklenecek Miktar' : 'Yeni Miktar'}
                  </label>
                  <input
                    type="number"
                    value={stockChangeValue}
                    onChange={(e) => setStockChangeValue(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder={
                      stockChangeType === 'add'
                        ? 'Örn: 10 (ekle) veya -5 (çıkar)'
                        : 'Örn: 50 (yeni stok)'
                    }
                    className="input"
                    step="1"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Preview */}
          {preview && preview.length > 0 && (
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Önizleme
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                {preview.slice(0, 5).map((item) => (
                  <div
                    key={item.id}
                    className="p-2 bg-background-tertiary rounded text-xs"
                  >
                    <div className="font-medium text-foreground truncate">
                      {item.name}
                    </div>
                    {updateType === 'price' &&
                      purchasePriceChangeValue && (
                        <div className="text-foreground-secondary">
                          Alış:{' '}
                          <span className="line-through">
                            {formatCurrency(item.unitPrice)}
                          </span>{' '}
                          →{' '}
                          <span className="text-accent font-medium">
                            {formatCurrency(item.newUnitPrice)}
                          </span>
                        </div>
                      )}
                    {updateType === 'price' &&
                      retailPriceChangeValue && (
                        <div className="text-foreground-secondary">
                          Satış:{' '}
                          <span className="line-through">
                            {formatCurrency(item.retailPrice)}
                          </span>{' '}
                          →{' '}
                          <span className="text-accent font-medium">
                            {formatCurrency(item.newRetailPrice)}
                          </span>
                        </div>
                      )}
                    {(updateType === 'stock') &&
                      stockChangeValue && (
                        <div className="text-foreground-secondary">
                          Stok:{' '}
                          <span className="line-through">{item.quantity}</span> →{' '}
                          <span className="text-accent font-medium">
                            {item.newStock}
                          </span>
                        </div>
                      )}
                  </div>
                ))}
                {preview.length > 5 && (
                  <div className="text-xs text-foreground-tertiary text-center">
                    +{preview.length - 5} ürün daha...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Update Button */}
          <button
            onClick={handleBulkUpdate}
            disabled={
              bulkUpdateModal.open ||
              selectedProducts.length === 0 ||
              (!purchasePriceChangeValue && !retailPriceChangeValue && !stockChangeValue)
            }
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {(updateType === 'price' ? bulkUpdatePrices.isPending : updateProduct.isPending) ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Güncelleniyor...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Güncelle ({selectedProducts.length} ürün)
              </>
            )}
          </button>
        </div>
      </div>

      {/* Güncelleme modalı: bitene kadar kapatılamaz, progress bar */}
      {bulkUpdateModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-update-title"
        >
          <div className="card p-6 w-full max-w-md mx-4 shadow-xl">
            <h2 id="bulk-update-title" className="text-lg font-semibold text-foreground mb-4">
              {bulkUpdateModal.done ? 'Tamamlandı' : 'Güncelleniyor...'}
            </h2>
            {!bulkUpdateModal.done ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <Loader2 className="w-6 h-6 text-accent animate-spin flex-shrink-0" />
                  <span className="text-sm text-foreground-secondary">
                    {bulkUpdateModal.progress} / {bulkUpdateModal.total} ürün
                  </span>
                </div>
                <div className="h-2 bg-background-tertiary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all duration-300 ease-out"
                    style={{
                      width: `${bulkUpdateModal.total ? (bulkUpdateModal.progress / bulkUpdateModal.total) * 100 : 0}%`,
                    }}
                  />
                </div>
                <p className="text-xs text-foreground-tertiary mt-4 text-center">
                  Ekranı terk etmemenizi tavsiye ederiz.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-foreground-secondary mb-2">
                  {bulkUpdateModal.failed === 0
                    ? `${bulkUpdateModal.succeeded} ürün başarıyla güncellendi.`
                    : `${bulkUpdateModal.succeeded} güncellendi, ${bulkUpdateModal.failed} başarısız.`}
                </p>
                {bulkUpdateModal.failMessage && (
                  <p className="text-xs text-error mb-4 line-clamp-2">{bulkUpdateModal.failMessage}</p>
                )}
                <button onClick={closeBulkUpdateModal} className="btn-primary w-full">
                  Kapat
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <Toast
        open={toast.open}
        onClose={() => setToast({ ...toast, open: false })}
        message={toast.message}
        severity={toast.severity}
      />
    </div>
  );
};

